import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Hospice, PSO, CPF } from '@mummssoftware/utils/constants';
import { PNCounterContext } from '@mummssoftware/global-context/PNCounter';
import { REFRESH_PN } from '@mummssoftware/global-context/PNCounter/constants';
import { OrdersGlobalContext } from '@mummssoftware/global-context/OrdersGlobalContext';
import { VFGlobalContext } from '@mummssoftware/global-context';
import {
  getUserCredentials,
  getLocalOrderTime,
  isFreqOrder,
} from '@mummssoftware/utils/orders/orders-utils';
import {
  ADM,
  LOC,
  OTH,
  MMD,
  FC,
  DEATH,
  REVOKED,
  TRANSFERRED,
  FOR_CAUSE,
  LEFT_GEOGRAPHIC_AREA,
  NON_CERTIFIED,
  mapTitle,
} from '@mummssoftware/utils/orders';
import { OrderNote } from './OrderNote';
import { ProgressNotesContext } from '../Context/ProgressNotesContext';
import SubmitNote from '../ProgressNotes/SubmitNote';
import { Context } from '../../context';
import { isDisType } from './utils';
import {
  GET_DISCHARGE_TYPES,
  SET_ORDER_CREATED,
  SAVE_PSO,
} from '../Context/constants';
import ApprovalStatement from './ApprovalStatement';
import GenericStatement from './GenericStatement';
import DischargeStatement from './DischargeStatement';
import {
  OrderInputWrapper,
  StatementDiv,
  StatementText,
  Text,
  SavingText,
} from '../../components/layout/WrapperDiv';
import defaults from '../../translations/en.json';

/*
1. OrderStatement is located in the NoteComponent becomes visible from the OrdersMenu
2. CreateOrder button triggers create order
3. ApprovalStatement UI is present for Orders
4. First API call is send approval for all order type
   If the call is successful (savedApproval) the next chain of API calls is triggered
5. Second chain of call is Admit/Discharge ... and get the order text to be saved
*/

const OrderStatement = () => {
  const {
    updatePnCount,
    orderCreated,
    disOrderMap,
    dispatch,
    savePSO,
    resetWorkflow,
    isPhysicianRoleGroup,
  } = useContext(ProgressNotesContext);
  const { orderType, savedNote, createOrder } = useContext(OrdersGlobalContext);
  const {
    patient,
    currentUser,
    agency,
    orderProcessed,
    orderProcessedMMD,
    orderProcessedPhysMMD,
    savedStandingOrders,
    getAllNotes,
    getPatientOrders,
    clearSavedNote,
  } = useContext(Context);
  const intl = useIntl();

  const pnContext = useContext(PNCounterContext);
  const { VFordersMode } = useContext(VFGlobalContext);

  const isDischargeType = isDisType(orderType);
  const isHospiceProgram = patient?.programSiteName === Hospice;

  // As of now initial PN is created with empty text
  // Then created note should reflect orderStatement

  const [isOrderSaved, setOrderSaved] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [showPSOLoader, setShowPSOLoader] = useState<boolean>(false);
  const userCredentials = getUserCredentials(currentUser);

  // refresh the count for tagged orders to MED, CPP
  const updateNotesCount = () => {
    if (updatePnCount && Object.keys(updatePnCount)) {
      Object.entries(updatePnCount).forEach((payload) => {
        pnContext.dispatch &&
          pnContext.dispatch({
            type: REFRESH_PN,
            payload: payload[1],
          });
      });
    }
  };

  const updateUI = () => {
    if (orderType === MMD) {
      // do nada
    } else {
      getOrders();
    }
  };

  // get the UI to update
  const getOrders = async () => {
    updateNotesCount();
    await getAllNotes(patient.patientNumber, agency);
    getPatientOrders(patient.patientNumber, agency);
    // reset value
    clearSavedNote();
    resetWorkflow();
  };

  useEffect(() => {
    if (isDischargeType) {
      dispatch({
        type: GET_DISCHARGE_TYPES,
        payload: true,
      });
    }
  }, []);

  useEffect(() => {
    // loading of the 2 loaders should be uninterrupted for PSO
    if (savedStandingOrders && savePSO && createOrder) {
      dispatch({
        type: SAVE_PSO,
        payload: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedStandingOrders, createOrder]);

  useEffect(() => {
    if (savedNote && savedNote.note && orderProcessed) {
      setOrderSaved(true);
      updateUI();
    } else {
      setOrderSaved(false);
    }
  }, [savedNote?.note.toString(), orderProcessed]);

  useEffect(() => {
    // process for MED orders - same refresh for both physician and non-physician
    const mmdProcessed =
      (orderProcessedMMD && !isPhysicianRoleGroup) ||
      (orderProcessedPhysMMD && isPhysicianRoleGroup);
    if (savedNote && savedNote.note && mmdProcessed) {
      getOrders();
    }
  }, [savedNote?.note.toString(), orderProcessedMMD, orderProcessedPhysMMD]);

  useEffect(() => {
    if (createOrder && !isOrderSaved) {
      setShowLoader(true);
    } else {
      setShowLoader(false);
    }
  }, [createOrder, isOrderSaved, VFordersMode]);

  useEffect(() => {
    if (savePSO && !savedStandingOrders) {
      setShowPSOLoader(true);
    } else {
      setShowPSOLoader(false);
    }
  }, [savePSO, savedStandingOrders]);

  if (savedNote && savedNote.note && orderProcessed && !orderCreated) {
    dispatch({
      type: SET_ORDER_CREATED,
      payload: true,
    });
  }

  const tzDate = getLocalOrderTime(currentUser);
  const psoNonPhysPayload = defaults.psoOrderText;
  const psoPhysPayload = intl.formatMessage(defaults.psoPhysOrderText, {
    userCredentials,
  });
  const psoPayload = isPhysicianRoleGroup ? psoPhysPayload : psoNonPhysPayload;
  const orderVerifiedPayload = intl.formatMessage(defaults.orderVerified, {
    userCredentials,
    signDate: tzDate,
  });
  const physOrderVerifiedPayload = intl.formatMessage(
    defaults.physOrderVerified,
    {
      userCredentials,
      signDate: tzDate,
    },
  );

  const OrderVerified = <Text bold>{orderVerifiedPayload}</Text>;
  const PhysOrderVerified = <Text bold>{physOrderVerifiedPayload}</Text>;
  const PhysIntro = <Text bold>I, {userCredentials}</Text>;
  const WaitingRcopia = <Text bold>{defaults.waitingRcopia}</Text>;
  const WaitingCPVF = <Text bold>{defaults.waitingCPVF}</Text>;
  const PSOtext = <Text>{psoPayload}</Text>;
  const INAtext = <Text>{defaults.admStatement.admINA}</Text>;

  const getDischargeStatement = () => {
    if (orderType && disOrderMap) {
      const isOrder = disOrderMap[orderType];
      if (isOrder) {
        if (isPhysicianRoleGroup) {
          return (
            <>
              <StatementText>
                {PhysIntro}
                <DischargeStatement isorder={isOrder} />
                {PhysOrderVerified}
                <OrderNote />
              </StatementText>
            </>
          );
        }
        return (
          <>
            <StatementText>
              <ApprovalStatement />
              <DischargeStatement isorder={isOrder} />
              {OrderVerified}
              <OrderNote />
            </StatementText>
          </>
        );
      }
      return (
        <>
          <StatementText>
            <DischargeStatement isorder={isOrder} />
            <OrderNote />
          </StatementText>
        </>
      );
    }
    return <></>;
  };

  const getStatement = (orderChoice: string | null) => {
    switch (orderChoice) {
      case OTH:
      case CPF:
      case FC:
      case LOC: {
        if (isPhysicianRoleGroup) {
          return (
            <>
              <StatementText>
                {PhysIntro}
                <GenericStatement />
                {PhysOrderVerified}
              </StatementText>
              <OrderNote />
            </>
          );
        }
        return (
          <>
            <StatementText>
              <ApprovalStatement />
              <GenericStatement />
              {OrderVerified}
            </StatementText>
            <OrderNote />
          </>
        );
      }

      case MMD: {
        if (isPhysicianRoleGroup) {
          return (
            <>
              <StatementText>
                {PhysIntro}
                <GenericStatement />
                {WaitingRcopia}
              </StatementText>
              <OrderNote />
            </>
          );
        }
        return (
          <>
            <StatementText>
              <ApprovalStatement />
              <GenericStatement />
              {WaitingRcopia}
            </StatementText>
            <OrderNote />
          </>
        );
      }
      case ADM: {
        return (
          <>
            <StatementText>
              <ApprovalStatement />
              <GenericStatement />
              {isHospiceProgram && INAtext}
              {OrderVerified}
              <OrderNote />
            </StatementText>
          </>
        );
      }

      case PSO: {
        if (isPhysicianRoleGroup) {
          return (
            <>
              <StatementText>
                <GenericStatement />
                {PSOtext}
                {PhysOrderVerified}
                <OrderNote />
              </StatementText>
            </>
          );
        }
        return (
          <>
            <StatementText>
              <ApprovalStatement />
              {PSOtext}
              {OrderVerified}
              <OrderNote />
            </StatementText>
          </>
        );
      }
      case FOR_CAUSE:
      case LEFT_GEOGRAPHIC_AREA:
      case NON_CERTIFIED:
      case DEATH:
      case REVOKED:
      case TRANSFERRED:
        return getDischargeStatement();
      default:
        return <ApprovalStatement />;
    }
  };

  const getOrderUI = () => (
    <OrderInputWrapper
      id="order-input"
      loading={!!createOrder || VFordersMode}
      hide={isFreqOrder(orderType)}
    >
      <SubmitNote
        orderText={orderType && mapTitle[orderType]}
        hide={createOrder}
      />
      {getStatement(orderType)}
    </OrderInputWrapper>
  );

  const getUI = () => (
    <>
      {!isDischargeType && getOrderUI()}
      {isDischargeType && !disOrderMap && <div>{defaults.loadingDis}</div>}
      {isDischargeType && disOrderMap && getOrderUI()}
    </>
  );

  return (
    <StatementDiv>
      {showLoader && <LinearProgress />}
      {showLoader && isFreqOrder(orderType) && (
        <SavingText>{defaults.saving}</SavingText>
      )}
      {showPSOLoader && <LinearProgress color="secondary" />}
      {getUI()}
    </StatementDiv>
  );
};

export default OrderStatement;
